import React, { useEffect, useState } from 'react'
import { Container, Typography } from '@cleartrip/ct-design-components'
import { isUserSignedIn } from '@/utils/general/user'
import { useAppSelector } from '@/redux/store/hooks'
import useWowPricingExperiment from '@/hooks/srp/useWowPricingExperiment'
import { Stencil } from '@/components/common/Stencil'
import { getProgressiveLoadingIndices, getWowLoadingFareIds } from '@/redux/slices/srpSlice'
import WowFareCalloutShared from '@/components/wowPricingComponents/WowFareCallout'

interface WowFareCalloutProps {
    /** Fares for the current card — used to read fareId + couponCallout. */
    listOfFares: any[]
    /** wowFares slice from Redux, keyed by fareId. */
    wowFares: Record<string, any>
    /** Card index in the SRP list — index 0 gets the auto-show tooltip. */
    cardIndex?: number
    /** True while the dynamic-content fetch is in flight (suppresses tooltip). */
    isDynamicContentLoading?: boolean
    /** True when this card came from the high-RPS list — suppress tooltip. */
    isHighRps?: boolean
    /** Whether this is an international flow. */
    isIntl?: boolean
    isSrpRevamp?: boolean
    labelStyle?: React.CSSProperties
    style?: React.CSSProperties
}

const WowFareCallout: React.FC<WowFareCalloutProps> = ({
    listOfFares = [],
    wowFares = {},
    cardIndex,
    isDynamicContentLoading = false,
    isHighRps = false,
    isIntl = false,
    isSrpRevamp = false,
    labelStyle,
    style,
}) => {
    const { wowOn } = useWowPricingExperiment(!!isIntl)
    const progressiveLoadingIndices = useAppSelector(getProgressiveLoadingIndices)
    const wowLoadingFareIds = useAppSelector(getWowLoadingFareIds)

    const { showPerAdultPricing, fareId, couponCallout = '' } = listOfFares?.[0] || {}
    const isLoggedIn = isUserSignedIn()

    // Show a shimmer in place of the callout when WOW data for this specific
    // card is in flight.
    const isLoadingForThisTuple =
        isDynamicContentLoading ||
        (typeof cardIndex === 'number' &&
            progressiveLoadingIndices &&
            progressiveLoadingIndices.includes(cardIndex)) ||
        (!!fareId && wowLoadingFareIds && wowLoadingFareIds.includes(fareId))

    if (wowOn && isLoadingForThisTuple) {
        return (
            <Container
                className={isSrpRevamp ? undefined : 'mt-1'}
                style={{ display: 'flex', justifyContent: 'flex-end' }}
            >
                <Stencil width={250} height={16} shimmer borderRadius={3} />
            </Container>
        )
    }

    const fare = wowFares?.[fareId]
    const { pricing } = fare || {}
    const pricingObject = showPerAdultPricing ? pricing?.perAdultPricing : pricing?.totalPricing
    const { couponDetails, wowApplicable = false } = pricingObject || {}
    const { message = '', wowDisplayPrice = 0 } = couponDetails || {}

    if (!isLoggedIn) {
        if (!couponCallout) {
            return null
        }
        return (
            <Container className="mt-1 flex flex-column flex-end">
                <Typography className="ta-right mb-1 t-truncate ws-nowrap" variant="B3" color="success">
                    {couponCallout}
                </Typography>
            </Container>
        )
    }

    // Signed-in but no applicable WOW message → render nothing.
    if (!wowApplicable && !message) {
        return null
    }

    const processedMessage = (message || '').replace('{couponCode}', couponDetails?.couponCode || '')

    return (
        <WowFareCalloutShared
            couponCallout={processedMessage}
            wowApplicable={wowApplicable}
            wowDisplayPrice={wowDisplayPrice}
            isIntl={isIntl}
            isSrpRevamp={isSrpRevamp}
            className={isSrpRevamp ? 'ta-right' : 'mt-1 ta-right'}
            labelStyle={labelStyle}
            style={style}
        />
    )
}

export default WowFareCallout
